/**
 * API hook for chart configurations
 */
import { useCallback } from "react";
import { API_BASE } from "../config";
import { useAuth } from "../auth/AuthProvider";
import type {
  ChartConfig,
  ChartType,
  CreateChartRequest,
  UpdateChartRequest,
  ChartsListResponse,
  ChartResponse,
} from "../types/charts";

/** Frontend-only chart types that the backend doesn't know about */
const FRONTEND_ONLY_TYPES = ["correlation", "topic_shift", "cooccurrence", "volatility"] as const;
type FrontendOnlyType = (typeof FRONTEND_ONLY_TYPES)[number];

/** Map frontend-only chart types to backend-compatible values */
function toBackend(req: CreateChartRequest | UpdateChartRequest): typeof req {
  if (req.chart_type && (FRONTEND_ONLY_TYPES as readonly string[]).includes(req.chart_type)) {
    return { ...req, chart_type: "stacked_bar", config: { ...req.config, fe_chart_type: req.chart_type } };
  }
  return req;
}

/** Restore frontend chart_type from backend config flag */
function fromBackend(chart: ChartConfig): ChartConfig {
  const cfg = chart.config as Record<string, unknown>;
  if (cfg?.fe_chart_type && (FRONTEND_ONLY_TYPES as readonly string[]).includes(cfg.fe_chart_type as string)) {
    return { ...chart, chart_type: cfg.fe_chart_type as ChartType };
  }
  // Legacy: support old "correlation: true" flag
  if (cfg?.correlation) {
    return { ...chart, chart_type: "correlation" as ChartType };
  }
  return chart;
}

export function useChartsApi(patientId: string) {
  const { ensureToken } = useAuth();

  const apiJson = useCallback(
    async <T>(path: string, init: RequestInit = {}): Promise<T> => {
      const token = await ensureToken();
      const resp = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(init.headers || {}),
        },
      });

      const text = await resp.text();
      let data: unknown = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        console.error("[useChartsApi] JSON parse failed:", { path, status: resp.status, text });
        throw new Error(`HTTP ${resp.status} (non-JSON response)`);
      }

      if (!resp.ok) {
        throw new Error((data as { error?: string })?.error || `HTTP ${resp.status}`);
      }
      return data as T;
    },
    [ensureToken]
  );

  const loadCharts = useCallback(
    async (signal?: AbortSignal): Promise<ChartConfig[]> => {
      if (!patientId?.trim()) return [];

      const qs = new URLSearchParams({ patient_id: patientId.trim() });
      const token = await ensureToken();
      const resp = await fetch(`${API_BASE}/charts?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      const text = await resp.text();
      let data: ChartsListResponse = { ok: false, items: [] };
      try {
        data = text ? JSON.parse(text) : { ok: false, items: [] };
      } catch {
        throw new Error(`HTTP ${resp.status} (non-JSON response)`);
      }

      if (!resp.ok) {
        throw new Error((data as { error?: string })?.error || `HTTP ${resp.status}`);
      }

      return (data.items || []).map(fromBackend);
    },
    [patientId, ensureToken]
  );

  const createChart = useCallback(
    async (req: CreateChartRequest): Promise<ChartConfig> => {
      const data = await apiJson<ChartResponse>("/charts", {
        method: "POST",
        body: JSON.stringify(toBackend(req)),
      });
      return fromBackend(data.item);
    },
    [apiJson]
  );

  const updateChart = useCallback(
    async (chartId: string, req: UpdateChartRequest): Promise<ChartConfig> => {
      const qs = new URLSearchParams({ patient_id: patientId.trim() });
      const data = await apiJson<ChartResponse>(`/charts/${chartId}?${qs.toString()}`, {
        method: "PUT",
        body: JSON.stringify(toBackend(req)),
      });
      return fromBackend(data.item);
    },
    [patientId, apiJson]
  );

  const deleteChart = useCallback(
    async (chartId: string): Promise<void> => {
      const qs = new URLSearchParams({ patient_id: patientId.trim() });
      await apiJson(`/charts/${chartId}?${qs.toString()}`, {
        method: "DELETE",
      });
    },
    [patientId, apiJson]
  );

  return {
    loadCharts,
    createChart,
    updateChart,
    deleteChart,
  };
}
